import React, { useState, useEffect, useRef, useCallback } from 'react';
import { supabase } from '../supabaseClient';
import { AppNotification, GlobalUser } from '../types';

interface UseUserNotificationsResult {
    notifications: AppNotification[];
    setNotifications: React.Dispatch<React.SetStateAction<AppNotification[]>>;
    markAsRead: (id: string) => Promise<void>;
    markAllAsRead: (projectId?: string) => Promise<void>;
    clearHistory: (projectId?: string) => Promise<void>;
    loading: boolean;
    refresh: () => Promise<void>;
}

export function useUserNotifications(currentUser?: GlobalUser): UseUserNotificationsResult {
    const [notifications, setNotifications] = useState<AppNotification[]>([]);
    const [loading, setLoading] = useState<boolean>(true);
    const prevUserIdRef = useRef<string | undefined>(currentUser?.id);

    // Helpers to convert DB row to AppNotification
    const mapRowToNotification = (row: any): AppNotification => ({
        id: row.id,
        userId: row.recipient_id,
        title: row.title,
        description: row.description,
        date: row.created_at,
        isRead: Boolean(row.is_read),
        type: (row.category || 'planning') as AppNotification['type'],
        link: row.link || undefined,
        projectId: row.project_id || undefined,
        projectName: row.project_name || undefined,
        requiresAttention: Boolean(row.requires_attention),
        eventType: row.event_type as AppNotification['eventType'],
        metadata: row.metadata || {}
    });

    const getStorageKey = useCallback((userId?: string) => {
        return userId ? `klyd_user_notifs_${userId}` : null;
    }, []);

    // 1. Isolation & Account Switching: Clean cache when user changes
    useEffect(() => {
        const prevId = prevUserIdRef.current;
        const currentId = currentUser?.id;

        if (prevId && prevId !== currentId) {
            // User switched or logged out: purge in-memory state and previous user cache
            setNotifications([]);
            try {
                const prevKey = getStorageKey(prevId);
                if (prevKey) localStorage.removeItem(prevKey);
            } catch (e) {
                console.warn('[Notifications] Error cleaning previous user cache:', e);
            }
        }
        prevUserIdRef.current = currentId;
    }, [currentUser?.id, getStorageKey]);

    // 2. Fetch Notifications for Current User (Protected by RLS)
    const fetchNotifications = useCallback(async () => {
        if (!currentUser?.id) {
            setNotifications([]);
            setLoading(false);
            return;
        }

        const storageKey = getStorageKey(currentUser.id);

        // Load local offline cache for immediate display
        if (storageKey) {
            try {
                const cached = localStorage.getItem(storageKey);
                if (cached) {
                    const parsed = JSON.parse(cached);
                    if (Array.isArray(parsed)) {
                        setNotifications(parsed);
                    }
                }
            } catch (e) {
                console.warn('[Notifications] Error reading offline cache:', e);
            }
        }

        try {
            setLoading(true);

            // Direct query to the secure relational table.
            // RLS automatically limits rows to the authenticated user.
            const { data, error } = await supabase
                .from('user_notifications')
                .select('*')
                .eq('is_deleted', false)
                .order('created_at', { ascending: false })
                .limit(100);

            if (error) {
                // If table is pending authorization or not yet applied (PGRST205),
                // degrade gracefully without crashing the app.
                if (error.code === 'PGRST205') {
                    console.info('[Notifications] Tabla user_notifications en espera de migración en BD.');
                } else {
                    console.warn('[Notifications] Error consultando notificaciones de usuario:', error.message);
                }
                return;
            }

            if (data && Array.isArray(data)) {
                const mapped = data.map(mapRowToNotification);
                setNotifications(mapped);

                if (storageKey) {
                    try {
                        localStorage.setItem(storageKey, JSON.stringify(mapped));
                    } catch (e) {
                        console.warn('[Notifications] Error writing offline cache:', e);
                    }
                }
            }
        } catch (err) {
            console.error('[Notifications] Excepción recuperando notificaciones:', err);
        } finally {
            setLoading(false);
        }
    }, [currentUser?.id, getStorageKey]);

    // Initial fetch on mount or user change
    useEffect(() => {
        fetchNotifications();
    }, [fetchNotifications]);

    // 3. Supabase Realtime Subscription (Strictly scoped by recipient)
    useEffect(() => {
        if (!currentUser?.id) return;

        const channelName = `notifs_realtime_${currentUser.id.substring(0, 12)}`;
        const channel = supabase.channel(channelName)
            .on(
                'postgres_changes',
                {
                    event: '*',
                    schema: 'public',
                    table: 'user_notifications'
                },
                (payload) => {
                    if (payload.eventType === 'INSERT') {
                        const newRow = payload.new as any;
                        if (!newRow || newRow.is_deleted) return;

                        // Verify recipient matches current user
                        const isForUser =
                            newRow.recipient_id === currentUser.id ||
                            newRow.recipient_email === currentUser.email;

                        if (isForUser) {
                            const newNotif = mapRowToNotification(newRow);
                            setNotifications(prev => [newNotif, ...prev.filter(n => n.id !== newNotif.id)]);
                        }
                    } else if (payload.eventType === 'UPDATE') {
                        const updatedRow = payload.new as any;
                        if (!updatedRow) return;

                        setNotifications(prev => {
                            if (updatedRow.is_deleted) {
                                return prev.filter(n => n.id !== updatedRow.id);
                            }
                            return prev.map(n => n.id === updatedRow.id ? mapRowToNotification(updatedRow) : n);
                        });
                    } else if (payload.eventType === 'DELETE') {
                        const deletedRow = payload.old as any;
                        if (deletedRow?.id) {
                            setNotifications(prev => prev.filter(n => n.id !== deletedRow.id));
                        }
                    }
                }
            )
            .subscribe();

        return () => {
            supabase.removeChannel(channel);
        };
    }, [currentUser?.id, currentUser?.email]);

    // 4. Local In-App Event Listener (Optimistic updates from local triggers)
    useEffect(() => {
        if (!currentUser) return;

        const handleLocalNotification = (e: any) => {
            const detail = e.detail as AppNotification[];
            if (Array.isArray(detail) && detail.length > 0) {
                // Only incorporate notifications meant for this user
                const forMe = detail.filter(n =>
                    n.userId === currentUser.id ||
                    n.userId === currentUser.email ||
                    n.userId === 'all'
                );

                if (forMe.length > 0) {
                    setNotifications(prev => {
                        const existingIds = new Set(prev.map(p => p.id));
                        const uniqueNew = forMe.filter(n => !existingIds.has(n.id));
                        const updated = [...uniqueNew, ...prev];
                        const storageKey = getStorageKey(currentUser.id);
                        if (storageKey) {
                            try {
                                localStorage.setItem(storageKey, JSON.stringify(updated.slice(0, 100)));
                            } catch { }
                        }
                        return updated;
                    });
                }
            }
        };

        window.addEventListener('klyd_app_notification_created', handleLocalNotification);
        return () => {
            window.removeEventListener('klyd_app_notification_created', handleLocalNotification);
        };
    }, [currentUser, getStorageKey]);

    // 5. Actions: markAsRead
    const markAsRead = useCallback(async (id: string) => {
        setNotifications(prev =>
            prev.map(n => n.id === id ? { ...n, isRead: true } : n)
        );

        if (currentUser?.id) {
            const storageKey = getStorageKey(currentUser.id);
            if (storageKey) {
                try {
                    const current = notifications.map(n => n.id === id ? { ...n, isRead: true } : n);
                    localStorage.setItem(storageKey, JSON.stringify(current));
                } catch { }
            }
        }

        try {
            // Try secure RPC first
            const { error: rpcError } = await supabase.rpc('mark_notification_as_read', {
                p_notification_id: id
            });

            if (rpcError) {
                // Fallback to direct update (protected by RLS)
                await supabase
                    .from('user_notifications')
                    .update({ is_read: true, read_at: new Date().toISOString() })
                    .eq('id', id);
            }
        } catch (e) {
            console.warn('[Notifications] Error marcando notificación como leída en BD:', e);
        }
    }, [currentUser?.id, getStorageKey, notifications]);

    // 6. Actions: markAllAsRead
    const markAllAsRead = useCallback(async (projectId?: string) => {
        setNotifications(prev =>
            prev.map(n => (!projectId || n.projectId === projectId) ? { ...n, isRead: true } : n)
        );

        if (currentUser?.id) {
            const storageKey = getStorageKey(currentUser.id);
            if (storageKey) {
                try {
                    const current = notifications.map(n => (!projectId || n.projectId === projectId) ? { ...n, isRead: true } : n);
                    localStorage.setItem(storageKey, JSON.stringify(current));
                } catch { }
            }
        }

        try {
            const { error: rpcError } = await supabase.rpc('mark_all_notifications_as_read', {
                p_project_id: projectId || null
            });

            if (rpcError) {
                let query = supabase
                    .from('user_notifications')
                    .update({ is_read: true, read_at: new Date().toISOString() })
                    .eq('is_read', false);

                if (projectId) {
                    query = query.eq('project_id', projectId);
                }
                await query;
            }
        } catch (e) {
            console.warn('[Notifications] Error marcando todas como leídas en BD:', e);
        }
    }, [currentUser?.id, getStorageKey, notifications]);

    // 7. Actions: clearHistory
    const clearHistory = useCallback(async (projectId?: string) => {
        setNotifications(prev =>
            prev.filter(n => !(n.isRead && (!projectId || n.projectId === projectId)))
        );

        if (currentUser?.id) {
            const storageKey = getStorageKey(currentUser.id);
            if (storageKey) {
                try {
                    const current = notifications.filter(n => !(n.isRead && (!projectId || n.projectId === projectId)));
                    localStorage.setItem(storageKey, JSON.stringify(current));
                } catch { }
            }
        }

        try {
            const { error: rpcError } = await supabase.rpc('clear_user_notification_history', {
                p_project_id: projectId || null
            });

            if (rpcError) {
                let query = supabase
                    .from('user_notifications')
                    .update({ is_deleted: true })
                    .eq('is_read', true);

                if (projectId) {
                    query = query.eq('project_id', projectId);
                }
                await query;
            }
        } catch (e) {
            console.warn('[Notifications] Error limpiando historial en BD:', e);
        }
    }, [currentUser?.id, getStorageKey, notifications]);

    return {
        notifications,
        setNotifications,
        markAsRead,
        markAllAsRead,
        clearHistory,
        loading,
        refresh: fetchNotifications
    };
}
